import PropTypes from "prop-types";
import { useState } from "react";
import { Link } from "gatsby";
import { formatISO, parse, format, addBusinessDays } from "date-fns";

import { useCurrentCountryCode } from "@hooks/usePrices";
import useDiscountsInfo, { DISCOUNT_INFO_MODES } from "@hooks/useDiscountsInfo";
import useDeviceDetect from "@hooks/useDeviceDetect";

import { ALTag } from "@components/ALComponents";
import ALShopifyImage from "@components/al_components/ALShopifyImage";
import PriceContainer from "@components/PriceContainer";
import Actions from "@components/sideCart/lineItem/Actions";
import LineItemImageMto from "@components/sideCart/LineItemImageMto";

import { ComponentType } from "@constants/ComponentType";
import { Country } from "@constants/Country";
import DISCOUNT_TYPES from "@constants/DiscountTypes";

import { getShipByDate, isBannedProduct } from "@helpers/product";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";

const formatRange = (d1, d2) => {
  const today = new Date();
  const date1 = addBusinessDays(today, d1);
  const date2 = addBusinessDays(today, d2);

  return `${format(date1, "M/dd")} - ${format(date2, "M/dd")}`;
};

function getEngravableText(lineItem) {
  let engravableText = lineItem.attributes.find((a) => a.key.includes("_mto_engraving"))?.value;
  const type = lineItem.attributes.find((a) => a.key.includes("_mto_type"))?.value;
  let engravableContent = <span>{engravableText}</span>;
  if (type === "initial_necklace" && engravableText) {
    engravableText = Array.from(engravableText).join(" · ");
    engravableContent = <span className="font-bold">{engravableText}</span>;
  }
  if (engravableText) {
    if (["1-3", "4-6", "6-9"].includes(lineItem?.variant?.title)) {
      return (
        <>
          <p className="typo-body mb-[3px]">Number of Characters: {lineItem?.variant?.title}</p>
          <p className="typo-body mb-[3px]">
            {type !== "initial_necklace" && <span>Text: </span>}
            {engravableContent}
          </p>
        </>
      );
    }
    return (
      <p className="typo-body mb-[3px]">
        {type !== "initial_necklace" && <span>Text: </span>}
        {engravableContent}
      </p>
    );
  }
  return null;
}

function getItemSubtitle(lineItem) {
  const shouldDisplayLetter =
    /^[A-Za-z\s]*$/.test(lineItem?.variant?.title) && lineItem.variant.title !== "Default Title";
  const shouldDisplayNumber =
    /[Nn]{1}umber/.test(lineItem.title) && lineItem.variant.title !== "Default Title";
  const shouldDisplaySize =
    /^(\d+(\.\d+)?"?|[A-Za-z]+\/[A-Za-z]+)$/.test(lineItem?.variant?.title) && !shouldDisplayNumber;
  const shouldDisplaySign = /[Zz]{1}odiac/.test(lineItem.variant.product.title);
  const shouldDisplayBirthstone =
    /[Bb]{1}irthstone/.test(lineItem.variant.product.title) &&
    lineItem.variant.title !== "Default Title" &&
    !lineItem.variant.product.title.includes("Memento");

  // Optional Engravable - Pebble MTOs
  if (lineItem?.variant?.title === "No") {
    return null;
  }

  if (shouldDisplaySign) {
    return <p className="typo-body mb-[3px]">Sign: {lineItem.variant.title}</p>;
  }

  if (shouldDisplaySize) {
    return <p className="typo-body mb-[3px]">Size: {lineItem.variant.title}</p>;
  }

  if (shouldDisplayBirthstone) {
    return <p className="typo-body mb-[3px]">Birthstone: {lineItem.variant.title}</p>;
  }

  if (shouldDisplayLetter) {
    return <p className="typo-body mb-[3px]">Letter: {lineItem.variant.title}</p>;
  }

  if (shouldDisplayNumber) {
    return <p className="typo-body mb-[3px]">Number: {lineItem.variant.title}</p>;
  }

  return null;
}

function LineItem({ lineItem, onRemove, toggleCart, isFreeGift, isLuxe, onSwapGift }) {
  const [mtoProducts, setMtoProducts] = useState(null);

  const { isMobile } = useDeviceDetect();
  const discountsInfo = useDiscountsInfo();
  const luxeDiscountsInfo = useDiscountsInfo(DISCOUNT_INFO_MODES.LUXE_ONLY);
  const countryCode = useCurrentCountryCode();
  const hasGiftOffer = isLuxe
    ? luxeDiscountsInfo.some(({ type }) => type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE)
    : discountsInfo.some(({ type }) => type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE);

  const isBannedFromFreeGift = isBannedProduct(
    lineItem.variant.product.tags,
    discountsInfo.filter(({ type }) => type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE)
  );

  const isBannedFromLuxe = isBannedProduct(
    lineItem.variant.product.tags,
    luxeDiscountsInfo.filter(({ type }) => type !== DISCOUNT_TYPES.GIFT_WITH_PURCHASE)
  );
  const isSoldOut = !!lineItem?.variant?.available;
  const isDiscontinued = lineItem.variant === null;
  let isFinalSale = false;
  let isMto = false;
  if (lineItem?.attributes) {
    isFinalSale = !!lineItem.attributes.find(
      (atr) => atr.key === "_final_sale" && atr.value === "true"
    );
    isMto =
      !!lineItem.attributes.find((atr) => atr.key === "_mto" && atr.value === "true") &&
      !!lineItem.attributes.find((atr) => atr.key === "_mto_custom" && atr.value === "true");
  }

  const { isPreOrder, preorderShipsByDate } = (lineItem.attributes || []).reduce(
    (acc, { key, value }) => {
      if (key === "_ships_by_pre_order" && !!value) {
        const shipsByDateAttr = parse(value, "MMM d, yyyy", new Date());
        if (!shipsByDateAttr) return acc;
        acc.preorderShipsByDate = getShipByDate(
          formatISO(shipsByDateAttr, { representation: "date" })
        );
      } else if (key === "_pre_order") {
        acc.isPreOrder = value === "true";
      }
      return acc;
    },
    { isPreOrder: false, preorderShipsByDate: null }
  );

  const isSolidGold = lineItem.variant.product.tags.includes("Components:Solid Gold");
  const isMTO = lineItem.variant.product.tags.includes("MTO");
  const showTag = isSolidGold || isMTO;

  let exclusionMention = "";
  if (countryCode === Country.US) {
    if (isBannedFromLuxe && !isBannedFromFreeGift) {
      exclusionMention = "Excluded from Luxe Offer";
    } else if (isBannedFromLuxe && isBannedFromFreeGift && !isFreeGift) {
      exclusionMention = "Excluded from Luxe & Gift Offers";
    } else if (hasGiftOffer && isBannedFromFreeGift && !isFreeGift) {
      exclusionMention = "Excluded from Free Gift Offer";
    }
  }
  if (isSolidGold && isLuxe) {
    exclusionMention = "5% Off Solid Gold";
  }

  let selectedVariantIds = null;
  let selectedVariantNames = null;
  let imgsMtoDetails = [];
  if (isMto) {
    const attributes = lineItem?.attributes || [];

    selectedVariantIds = attributes
      .filter((a) => a.key.includes("_mto_variant_id_"))
      .map((a) => a.value);

    selectedVariantNames = attributes
      .filter((a) => a.key.includes("_mto_variant_name_"))
      .map((a) => a.value.toLowerCase());

    imgsMtoDetails = (selectedVariantNames || []).map((name) =>
      mtoProducts?.variantsPreview.find((v) => v.title.toLowerCase() === name.toLowerCase())
    );
  }

  return (
    <div className="flex pb-[20px] border-b border-light-grey">
      <div className="relative ml-[15px] desktop:ml-[20px] w-[24.27%] desktop:min-w-[24.67%]">
        {isFreeGift && (
          <div
            className={cn(
              "typo-micro-bold-caps px-[7px] py-[3px] absolute rounded-[4px] z-10",
              "-top-3 left-1/2 -translate-x-1/2 whitespace-nowrap",
              "text-secondary bg-white",
              "border border-secondary"
            )}
            data-testid="al-tag-cart"
          >
            Free Gift
          </div>
        )}
        {showTag && (
          <ALTag
            product={{
              node: { ...lineItem.variant.product, variants: [lineItem.variant] },
              contentful: null,
            }}
            componentType={ComponentType.SIDE_CART}
          />
        )}
        {/* IMAGE */}
        <div
          className={cn({ "border border-black": showTag, "border border-secondary": isFreeGift })}
        >
          {isDiscontinued ? (
            <p className="mr-[4px] pb-[3px] uppercase">Discontinued</p>
          ) : (
            <div className="block overflow-hidden">
              {isMto ? (
                <LineItemImageMto
                  lineItem={lineItem}
                  width={isMobile ? 140 : 180}
                  selectedVariantIds={selectedVariantIds}
                  setMtoProducts={setMtoProducts}
                />
              ) : (
                <ALShopifyImage
                  className="aspect-[0.8] scale-[1.4]"
                  url={lineItem.variant.image.url}
                  alt={lineItem.variant.image.altText}
                  width={isMobile ? 140 : 180}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col mr-[15px] desktop:mr-[20px] justify-between ml-[15px] ">
        <div className="w-full flex justify-between items-start">
          <div>
            <Link
              className="block typo-body-bold mb-[3px]"
              to={`/products/${lineItem.variant.product.handle}`}
              onClick={toggleCart}
            >
              {lineItem.variant.product.title.split(" - ")[1]}
            </Link>

            {getItemSubtitle(lineItem)}

            {isFreeGift && (
              <div className="typo-body text-secondary mb-[3px]">Low stock alert!</div>
            )}

            {lineItem.bogoApplied && (
              <div className="typo-body text-primary mb-[3px]">Discount applied</div>
            )}

            {isMto && (
              <div className="flex gap-[5px]">
                {imgsMtoDetails?.map((imgDetail, i) => (
                  <div key={`${imgDetail?.title}-${i}`} className="mb-[3px]">
                    <img
                      src={imgDetail?.file?.url}
                      alt={imgDetail?.title}
                      className="w-[16px] h-auto"
                    />
                  </div>
                ))}
              </div>
            )}

            {isMto && getEngravableText(lineItem)}

            {isFinalSale && <p className="typo-body text-secondary mb-[3px]">Final Sale</p>}

            {isPreOrder && <p className="typo-body mb-[3px]">Ships by {preorderShipsByDate}</p>}

            {lineItem?.attributes?.find((e) => e.key === "_mto_timeline") && (
              <p className="typo-body mb-[3px]">
                Delivery by{" "}
                {formatRange(
                  lineItem?.attributes
                    ?.find((e) => e.key === "_mto_timeline")
                    .value.split(" ")[0]
                    .split("-")[0],
                  lineItem?.attributes
                    ?.find((e) => e.key === "_mto_timeline")
                    .value.split(" ")[0]
                    .split("-")[1]
                )}
              </p>
            )}

            {exclusionMention && <p className="typo-body mb-[3px]">{exclusionMention}</p>}
          </div>

          <PriceContainer
            selectedVariant={lineItem.variant}
            productId={lineItem.variant.product.id}
            componentType={ComponentType.SIDE_CART}
            isFree={isFreeGift}
          />
        </div>

        {isSoldOut && <p className="typo-body-bold">Out of stock</p>}

        <Actions
          lineItem={lineItem}
          isFreeGift={isFreeGift}
          onRemove={onRemove}
          onSwapGift={onSwapGift}
        />
      </div>
    </div>
  );
}

LineItem.propTypes = {
  lineItem: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  toggleCart: PropTypes.func.isRequired,
  isFreeGift: PropTypes.bool,
  isLuxe: PropTypes.bool,
  onSwapGift: PropTypes.func,
};

export default withALErrorBoundary({
  name: "LineItem",
  priority: "P1",
})(LineItem);
